// Modules
import useSWR from "swr";
// API
import api from "@/helpers/api";
// Actions
import { getData } from "../getDataAction";
// Helpers
import { endSpinner, startSpinner } from "@/config/store/spinnerReducer";
import { sortByLocalCompare } from "@/helpers/sort";
import type { TherapistWithEvents } from "@/lib/types/therapist/calendar";

/*
 * Therapists with Events
 */
export const getTherapistsWithEvents = () => {
    const fetcher = url =>
        getData<TherapistWithEvents[]>({
            displaySpinner: false,
            url,
            sortDataBy: (...props) => sortByLocalCompare(...props, "name")
        });

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data, mutate } = useSWR("calendar/therapists/all-therapists-events", fetcher, {
        fallbackData: [],
        revalidateOnFocus: false
    });

    return { therapistsWithEvents: data, refreshTherapistsWithEvents: mutate };
};

export const getEventsByTherapistId = ({
    therapistId,
    params,
    revalidateOnFocus = false,
    fullScreenSpinner = false,
    displaySpinner = true
}) => {
    const fetcher = (url: string) =>
        getData({
            displaySpinner,
            fullScreenSpinner,
            url,
            sortDataBy: (...props) => sortByLocalCompare(...props, "start"),
            params
        });

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data, mutate } = useSWR(`calendar/therapists/${therapistId}/events/therapist-events`, fetcher, {
        fallbackData: [],
        revalidateOnFocus
    });

    const events = data?.map(entry => ({
        ...entry,
        start: new Date(entry.start),
        end: new Date(entry.end)
    }));

    return { events, refreshTherapistEvents: mutate };
};

/*
 * Events CRUD Operations
 */

export const createEventData = async ({ therapistId, data, dispatch, errorCb, successCb }) => {
    dispatch(startSpinner({ fullScreen: true }));

    return await api
        .post({ path: `calendar/therapists/${therapistId}/events/create`, data })
        .then(res => {
            successCb && successCb(res.data.eventId);
            dispatch(endSpinner());
        })
        .catch(err => {
            console.error("Error creating an event:", err);

            const errorMessage = `error-calendar-event-creation-${err.response?.status}`;

            errorCb && errorCb(errorMessage);
            dispatch(endSpinner());
        });
};

export const updateEventData = async ({ therapistId, eventId, params, data, dispatch, successCb, errorCb }) => {
    dispatch(startSpinner({ fullScreen: true }));

    return await api
        .put({ path: `calendar/therapists/${therapistId}/events/${eventId}/update`, data, params })
        .then(() => {
            successCb && successCb();
            dispatch(endSpinner());
        })
        .catch(err => {
            console.error("Error updating an event:", err);

            const errorMessage = `error-calendar-event-update-${err.response?.status}`;

            errorCb && errorCb(errorMessage);
            dispatch(endSpinner());
        });
};

export const deleteEventData = async ({ therapistId, eventId, dispatch, errorCb, successCb }) => {
    dispatch(startSpinner({ fullScreen: true }));

    return await api
        .delete({ path: `calendar/therapists/${therapistId}/events/${eventId}/delete` })
        .then(() => {
            successCb && successCb();
            dispatch(endSpinner());
        })
        .catch(err => {
            console.error("Error deleting an event:", err);
            const errorMessage = `error-calendar-event-delete-${err.response?.status}`;

            errorCb && errorCb(errorMessage);
            dispatch(endSpinner());
        });
};

/*
 * Absence CRUD Operations
 */

export const createTherapistAbsence = async ({ data, dispatch, errorCb, successCb }) => {
    dispatch(startSpinner());

    return await api
        .post({ path: `calendar/therapists/absence/create`, data })
        .then(() => {
            successCb && successCb();
            dispatch(endSpinner());
        })
        .catch(err => {
            console.error("Error creating absence for therapist:", err);

            errorCb && errorCb(err);
            dispatch(endSpinner());
        });
};

export const updateTherapistAbsence = async ({ therapistId, absenceId, dispatch, data, errorCb, successCb }) => {
    dispatch(startSpinner());

    return await api
        .put({ path: `calendar/therapists/${therapistId}/absence/${absenceId}`, data })
        .then(() => {
            successCb && successCb();
            dispatch(endSpinner());
        })
        .catch(err => {
            console.error("Error updating absence for therapist:", err);

            errorCb && errorCb(err);
            dispatch(endSpinner());
        });
};

export const deleteTherapistAbsence = async ({ therapistId, absenceId, dispatch, errorCb, successCb }) => {
    dispatch(startSpinner());

    return await api
        .delete({ path: `calendar/therapists/${therapistId}/absence/${absenceId}` })
        .then(() => {
            successCb && successCb();
            dispatch(endSpinner());
        })
        .catch(err => {
            console.error("Error deleting absence for therapist:", err);

            errorCb && errorCb(err);
            dispatch(endSpinner());
        });
};
